/**
 * A proposed change to a team someone else owns (docs/plans/2026-10-teams-one-owner.md in the workbench
 * repository, "AIOE's half"; AIOE 0.58.0). AIOE's own contract: the console and the workbench read it.
 *
 * Every organisation team has one owner (`owner` on orgTeamsV2.ts): the person who made it, until a
 * portfolio manager or an administrator hands it on. With the organisation's policy clause
 * `teams.changesNeedOwner` on, a save by an editor who is not the owner does not change the team: it
 * becomes a proposal, the owner gets an inbox record (Accept / Decline, served by the platform) and the
 * save answers 202 `{ state: "proposed", proposalId, team }` with the team as it stands. Accepting saves
 * the proposed fields against the team's current version the same way a late save would (a field the
 * team changed since the proposal was made stands as the team has it, listed in `refused`); a second
 * proposal by the same person on the same team supersedes the first.
 *
 *   PATCH /portfolio/v1/teams/:id/owner { userId }                 → the team (owner, administrator, portfolio manager)
 *   GET   /portfolio/v1/teams/:id/proposals[?state=pending|all]     { proposals }
 *   POST  /portfolio/v1/teams/:id/proposals/:pid/accept             { proposal, team, refused }
 *   POST  /portfolio/v1/teams/:id/proposals/:pid/decline { reason? } { proposal }
 *   GET   /admin/v1/people/:userId/owned-teams                      { teams } — a leaver's, to hand on
 */
import { z } from "zod";
export const TEAM_PROPOSAL_STATES = ["pending", "accepted", "declined", "superseded"];
export const teamProposalStateSchema = z.enum(TEAM_PROPOSAL_STATES);
/** One field the proposal would change: its path in the team (`members.Coder.model`, `settings.guidelines`), what the team has and what is proposed. */
export const teamProposalChangeSchema = z.object({
    path: z.string().min(1).max(300),
    from: z.unknown().optional(),
    to: z.unknown().optional(),
});
export const teamProposalSchema = z.object({
    id: z.string().min(1).max(100),
    teamId: z.string().min(1).max(100),
    teamName: z.string().max(200),
    /** The team's version the proposal was made against. */
    baseVersion: z.number().int().nonnegative(),
    proposedBy: z.string().min(1).max(200),
    proposedByName: z.string().max(200).optional(),
    proposedAt: z.string(),
    /** The team as the proposer would have it (the body their save sent, overlaid on the team as it stood). */
    team: z.record(z.string(), z.unknown()),
    changes: z.array(teamProposalChangeSchema).max(200),
    state: teamProposalStateSchema,
    decidedBy: z.string().max(200).optional(),
    decidedByName: z.string().max(200).optional(),
    decidedAt: z.string().optional(),
    /** The owner's words on a decline. */
    reason: z.string().max(2000).optional(),
    /** On accept: the team's version the change landed in. */
    landedVersion: z.number().int().positive().optional(),
});
/** A save that became a proposal: 202. */
export const teamProposedAnswerSchema = z.object({
    state: z.literal("proposed"),
    proposalId: z.string(),
    /** Who decides, in words, for "sent to Ana Lopez to accept". */
    owner: z.object({ id: z.string(), name: z.string().optional() }),
    /** The team as it stands (unchanged). */
    team: z.record(z.string(), z.unknown()),
});
/** `PATCH /portfolio/v1/teams/:id/owner`. */
export const teamOwnerSetSchema = z.object({ userId: z.string().min(1).max(200) });
/** `POST …/proposals/:pid/decline`. */
export const teamProposalDeclineSchema = z.object({ reason: z.string().max(2000).optional() });
/** The inbox record's action, served by the platform: `{ proposalId, decision: "accepted" | "declined" }`. */
export const TEAM_PROPOSAL_DECIDE_RPC = "aioe.team.proposal.decide";
export const teamProposalDedupeKey = (proposalId) => `team-proposal:${proposalId}`;
/** The organisation's policy clause (a governed setting by the workbench's key) that turns proposals on. Default off. */
export const TEAMS_CHANGES_NEED_OWNER_KEY = "teams.changesNeedOwner";
//# sourceMappingURL=teamProposals.js.map